import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { THEME } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
import type { TouchpointTopAnswerComponent } from '@/lib/api/types';

interface TopAnswersChartProps {
  components: TouchpointTopAnswerComponent[];
  height?: number;
}

export function TopAnswersChart({
  components,
  height = 240,
}: TopAnswersChartProps) {
  const t = useT();
  const [activeId, setActiveId] = useState<number | null>(
    components[0]?.componentId ?? null,
  );

  if (components.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-[var(--color-fg-muted)]">
        {t('stations.detail.dashboardEmpty')}
      </p>
    );
  }

  const active =
    components.find((c) => c.componentId === activeId) ?? components[0];

  // Truncate long option labels so the y-axis stays readable.
  const data = active.options.map((o) => ({
    name: o.value.length > 30 ? `${o.value.slice(0, 30)}…` : o.value,
    count: o.count,
  }));

  return (
    <div className="space-y-3">
      {components.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {components.map((c) => (
            <button
              key={c.componentId}
              type="button"
              onClick={() => setActiveId(c.componentId)}
              className={
                c.componentId === active.componentId
                  ? 'rounded-full bg-[var(--color-accent)] px-3 py-1 text-xs font-medium text-white'
                  : 'rounded-full border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-1 text-xs text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]'
              }
            >
              {c.componentLabel}
            </button>
          ))}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 16, bottom: 8, left: 16 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
          <XAxis
            type="number"
            allowDecimals={false}
            tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            dataKey="name"
            type="category"
            width={160}
            tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-bg-elevated)' }}
            contentStyle={{
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Bar dataKey="count" fill={THEME.accent} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
